import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useLocation } from 'react-router';
import {
  CreateNestedTokenPayload,
  CreateRootTokenPayload,
  FormTokenChainContextType,
  FormTokenChainState,
  FormTokenNode,
} from '../types/context';
import { useUserState } from '../hooks/context/useUserState';
import {
  FORM_TOKEN_EXPIRATION_TIME,
  FORM_TOKEN_SMALLER_EXPIRATION_TIME,
  FOUR_HOURS_LOOP_TIME,
  SET_FORM_TOKENS,
  EMPTY_FORM_TOKEN_CHAIN,
} from '../constants';
import { getRandomId } from '../utils/getRandomId';
import { GlobalLocationStateType } from '../types/types';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('formTokens');

export const defaultValues: FormTokenChainContextType = {
  isProvided: false,
  formTokens: EMPTY_FORM_TOKEN_CHAIN,
  getToken: () => {
    throw new Error('getToken must be implemented');
  },
  createRootToken: () => {
    throw new Error('createRootToken must be implemented');
  },
  createNestedToken: () => {
    throw new Error('createNestedToken must be implemented');
  },
  updateTokenValues: () => {
    throw new Error('updateTokenValues must be implemented');
  },
  popToken: () => {
    throw new Error('popToken must be implemented');
  },
  deleteChainFrom: () => {
    throw new Error('deleteChainFrom must be implemented');
  },
  cleanupExpiredTokens: () => {
    throw new Error('cleanupExpiredTokens must be implemented');
  },
  removeDescendantTokens: () => {
    throw new Error('removeDescendantTokens must be implemented');
  },
  updateParentTokenAndPopActualToken: () => {
    throw new Error('updateParentTokenAndPopActualToken must be implemented');
  },
  getActualTokenFromUrl: () => {
    throw new Error('getActualTokenFromUrl must be implemented');
  },
  setSmallExpirationTimeToTokenChain: () => {
    throw new Error('setSmallExpirationTimeToTokenChain must be implemented');
  },
};

export const FormTokenChainContext =
  createContext<FormTokenChainContextType>(defaultValues);

const FormTokenChainProvider = ({ children }: { children: ReactNode }) => {
  const { state: userState, dispatch: userDispatch } = useUserState();
  const { search, pathname } = useLocation<GlobalLocationStateType>();

  const getToken = (tokenId: string) => {
    return userState.formTokens?.nodes[tokenId] || null;
  };

  const getTokenFromSource = (tokenId: string, source: FormTokenChainState) => {
    return source.nodes[tokenId] || null;
  };

  const createRootToken = useCallback(
    (payload: CreateRootTokenPayload) => {
      const tokenId = payload.id ?? getRandomId();

      const token: FormTokenNode = {
        id: tokenId,
        parentTokenId: null,
        childTokenId: null,
        expiresAt: Date.now() + FORM_TOKEN_EXPIRATION_TIME,
        url: payload.id ? payload.url : payload.url + '?token=' + tokenId,
        fromUrl: payload.fromUrl,
        meta: payload.meta ?? {},
        values: {},
      };

      const newRootTokensArray = [...userState.formTokens.roots, tokenId];

      const newFormTokenChainState: FormTokenChainState = {
        nodes: {
          ...userState.formTokens.nodes,
          [tokenId]: token,
        },
        roots: newRootTokensArray,
      };

      /** @debugFeature formTokens Logs a created root form token: id, urls, meta, expiration and the new root count. */
      log('root token created', () => ({
        id: token.id,
        url: token.url,
        fromUrl: token.fromUrl,
        meta: token.meta,
        expiresAt: token.expiresAt,
        rootsCount: newRootTokensArray.length,
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
      return token;
    },
    [userDispatch, userState]
  );

  const createNestedToken = useCallback(
    (parentTokenId: string, payload: CreateNestedTokenPayload) => {
      const parentToken = getToken(parentTokenId);
      const childTokenId = getRandomId();

      if (parentToken === null) {
        throw new Error(`Parent token ${parentTokenId} not found`);
      }

      const updatedParentNode: FormTokenNode = {
        ...parentToken,
        childTokenId,
      };

      const nestedToken: FormTokenNode = {
        id: childTokenId,
        parentTokenId,
        childTokenId: null,
        expiresAt: Date.now() + FORM_TOKEN_EXPIRATION_TIME,
        url: payload.url + '?token=' + childTokenId,
        fromUrl: parentToken.url,
        values: payload.values ?? {},
        meta: payload.meta ?? {},
      };

      const newFormTokenChainState: FormTokenChainState = {
        nodes: {
          ...userState.formTokens.nodes,
          [parentTokenId]: updatedParentNode,
          [childTokenId]: nestedToken,
        },
        roots: userState.formTokens.roots,
      };

      /** @debugFeature formTokens Logs a created nested form token: id, parent id, url and the keys of its initial values. */
      log('nested token created', () => ({
        id: nestedToken.id,
        parentTokenId,
        url: nestedToken.url,
        valueKeys: Object.keys(nestedToken.values),
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
      return nestedToken;
    },
    [
      userDispatch,
      userState.formTokens.nodes,
      userState.formTokens.roots,
      getToken,
    ]
  );

  const updateTokenValues = useCallback(
    (token: FormTokenNode) => {
      const newFormTokenChainState: FormTokenChainState = {
        nodes: {
          ...userState.formTokens.nodes,
          [token.id]: token,
        },
        roots: userState.formTokens.roots,
      };

      /** @debugFeature formTokens Logs a token whose values were updated — the token id and the keys of its values (never the values themselves). */
      log('token values updated', () => ({
        id: token.id,
        valueKeys: Object.keys(token.values),
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
    },
    [userDispatch, userState.formTokens.nodes, userState.formTokens.roots]
  );

  const updateParentTokenAndPopActualToken = useCallback(
    (parentTokentoUpdate: FormTokenNode, actualTokenIdToPop: string) => {
      // Update token
      let newFormTokenChainState: FormTokenChainState = {
        nodes: {
          ...userState.formTokens.nodes,
          [parentTokentoUpdate.id]: parentTokentoUpdate,
        },
        roots: userState.formTokens.roots,
      };

      // Pop token
      const tokenToPop = getTokenFromSource(
        actualTokenIdToPop,
        newFormTokenChainState
      );

      if (tokenToPop === null) {
        throw new Error(`Token ${actualTokenIdToPop} not found`);
      }

      if (tokenToPop.childTokenId !== null) {
        throw new Error(`Token ${actualTokenIdToPop} has a child token`);
      }

      const { [actualTokenIdToPop]: deletedToken, ...restNodes } =
        newFormTokenChainState.nodes;

      newFormTokenChainState = {
        nodes: {
          ...restNodes,
        },
        roots: newFormTokenChainState.roots,
      };

      // If the deleted token has a parent, update the parent node
      if (deletedToken.parentTokenId !== null) {
        const parentToken = getTokenFromSource(
          deletedToken.parentTokenId,
          newFormTokenChainState
        );
        const updatedParentNode: FormTokenNode = {
          ...parentToken,
          childTokenId: null,
        };

        newFormTokenChainState = {
          nodes: {
            ...newFormTokenChainState.nodes,
            [deletedToken.parentTokenId]: updatedParentNode,
          },
          roots: newFormTokenChainState.roots,
        };
      }

      // If deleted token is root token
      if (deletedToken.parentTokenId === null) {
        const newRoots = newFormTokenChainState.roots.filter(
          (rootId) => rootId !== actualTokenIdToPop
        );
        newFormTokenChainState = {
          nodes: newFormTokenChainState.nodes,
          roots: newRoots,
        };
      }

      /** @debugFeature formTokens Logs a combined parent update + pop of the actual token: both token ids and whether the popped token was a root. */
      log('parent token updated and actual token popped', () => ({
        parentTokenId: parentTokentoUpdate.id,
        poppedTokenId: actualTokenIdToPop,
        wasRoot: deletedToken.parentTokenId === null,
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
    },
    [userDispatch, userState.formTokens.nodes, userState.formTokens.roots]
  );

  const popToken = useCallback(
    (currentTokenId: string) => {
      const currentToken = getToken(currentTokenId);

      if (currentToken === null) {
        throw new Error(`Token ${currentTokenId} not found`);
      }

      if (currentToken.childTokenId !== null) {
        throw new Error(`Token ${currentTokenId} has a child token`);
      }

      const { [currentTokenId]: deletedToken, ...restNodes } =
        userState.formTokens.nodes;

      let newFormTokenChainState: FormTokenChainState = {
        nodes: {
          ...restNodes,
        },
        roots: userState.formTokens.roots,
      };

      // If the deleted token has a parent, update the parent node
      if (deletedToken.parentTokenId !== null) {
        const parentToken = getToken(deletedToken.parentTokenId);
        const updatedParentNode: FormTokenNode = {
          ...parentToken,
          childTokenId: null,
        };

        newFormTokenChainState = {
          nodes: {
            ...newFormTokenChainState.nodes,
            [deletedToken.parentTokenId]: updatedParentNode,
          },
          roots: newFormTokenChainState.roots,
        };
      }

      // If deleted token is root token
      if (deletedToken.parentTokenId === null) {
        const newRoots = userState.formTokens.roots.filter(
          (rootId) => rootId !== currentTokenId
        );
        newFormTokenChainState = {
          nodes: newFormTokenChainState.nodes,
          roots: newRoots,
        };
      }

      /** @debugFeature formTokens Logs a popped token: its id and whether it was a root or was decoupled from a parent. */
      log('token popped', () => ({
        id: currentTokenId,
        wasRoot: deletedToken.parentTokenId === null,
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
    },
    [
      userDispatch,
      userState.formTokens.nodes,
      userState.formTokens.roots,
      getToken,
      userDispatch,
    ]
  );

  const deleteChainFrom = useCallback(
    (tokenId: string) => {
      const token = getToken(tokenId);

      if (token === null) {
        throw new Error(`Token ${tokenId} not found`);
      }

      let newFormTokenChainState: FormTokenChainState = {
        nodes: userState.formTokens.nodes,
        roots: userState.formTokens.roots,
      };

      // If the token has a parent, decouple it from the parent
      if (token.parentTokenId !== null) {
        const parentToken = getToken(token.parentTokenId);

        newFormTokenChainState = {
          nodes: {
            ...newFormTokenChainState.nodes,
            [token.parentTokenId]: {
              ...parentToken,
              childTokenId: null,
            },
          },
          roots: newFormTokenChainState.roots,
        };
      }

      const tokenToDeleteArray = [tokenId];

      // Recursively fill the tokenToDeleteArray with all descendants.
      const findTokensToDelete = (tokenIdToProcess: string) => {
        const tokenToProcess = getToken(tokenIdToProcess);

        if (tokenToProcess === null) {
          throw new Error(`Token ${tokenIdToProcess} not found`);
        }

        tokenToDeleteArray.push(tokenToProcess.id);

        if (tokenToProcess.childTokenId !== null) {
          findTokensToDelete(tokenToProcess.childTokenId);
        }
      };

      if (token.childTokenId !== null) {
        findTokensToDelete(token.childTokenId);
      }

      const newRoots = newFormTokenChainState.roots.filter(
        (rootId) => !tokenToDeleteArray.includes(rootId)
      );

      const newNodes = Object.fromEntries(
        Object.entries(newFormTokenChainState.nodes).filter(
          ([tokenId]) => !tokenToDeleteArray.includes(tokenId)
        )
      );

      newFormTokenChainState = {
        nodes: newNodes,
        roots: newRoots,
      };

      /** @debugFeature formTokens Logs a deleted token chain: the starting token id and the ids of all deleted tokens (the token and its descendants). */
      log('token chain deleted', () => ({
        fromTokenId: tokenId,
        deletedTokenIds: tokenToDeleteArray,
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
    },
    [userDispatch, userState.formTokens.nodes, userState.formTokens.roots]
  );

  const removeDescendantTokens = useCallback(
    (tokenId: string) => {
      const token = getToken(tokenId);

      if (token === null) {
        throw new Error(`Token ${tokenId} not found`);
      }

      if (token.childTokenId !== null) {
        deleteChainFrom(token.childTokenId);
      }
    },
    [
      deleteChainFrom,
      userDispatch,
      userState.formTokens.nodes,
      userState.formTokens.roots,
    ]
  );

  const cleanupExpiredTokens = useCallback(() => {
    const now = Date.now();
    const expiredTokens = Object.values(userState.formTokens.nodes).filter(
      (token) => token.expiresAt < now
    );

    if (expiredTokens.length > 0) {
      /** @debugFeature formTokens Logs the cleanup of expired tokens (id and expiration of each); silent when nothing expired. */
      log('expired tokens cleaned up', () =>
        expiredTokens.map((token) => ({
          id: token.id,
          expiresAt: token.expiresAt,
        }))
      );
    }

    let newFormTokenChainState: FormTokenChainState = {
      nodes: userState.formTokens.nodes,
      roots: userState.formTokens.roots,
    };

    expiredTokens.forEach((token) => {
      // If the token has a parent, decouple it from the parent
      if (token.parentTokenId !== null) {
        const parentToken = getToken(token.parentTokenId);
        const updatedParentNode: FormTokenNode = {
          ...parentToken,
          childTokenId: null,
        };
        newFormTokenChainState = {
          nodes: {
            ...newFormTokenChainState.nodes,
            [token.parentTokenId]: updatedParentNode,
          },
          roots: newFormTokenChainState.roots,
        };
      }

      // Delete the expired token
      const newNodes = Object.fromEntries(
        Object.entries(newFormTokenChainState.nodes).filter(
          ([tokenId]) => tokenId !== token.id
        )
      );

      // Delete the expired token from the roots if it is a root token
      const newRoots = newFormTokenChainState.roots.filter(
        (rootId) => rootId !== token.id
      );

      newFormTokenChainState = {
        nodes: newNodes,
        roots: newRoots,
      };
    });

    userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
  }, [userDispatch, userState]);

  const setSmallExpirationTimeToTokenChain = useCallback(
    (actualTokenId: string) => {
      const actualToken = getToken(actualTokenId);

      if (actualToken === null) {
        throw new Error(`Token ${actualTokenId} not found`);
      }

      const expirationTime = Date.now() + FORM_TOKEN_SMALLER_EXPIRATION_TIME;
      const countDebug = debugLogger.isEnabled('formTokens');
      let updatedTokenCount = 0;

      let newFormTokenChainState: FormTokenChainState = {
        nodes: userState.formTokens.nodes,
        roots: userState.formTokens.roots,
      };

      const updateState = (tokenId: string) => {
        const token = newFormTokenChainState.nodes[tokenId];
        if (countDebug) {
          updatedTokenCount += 1;
        }

        newFormTokenChainState = {
          nodes: {
            ...newFormTokenChainState.nodes,
            [tokenId]: {
              ...token,
              expiresAt: expirationTime,
            },
          },
          roots: newFormTokenChainState.roots,
        };

        if (token.parentTokenId !== null) {
          updateState(token.parentTokenId);
        }
      };

      updateState(actualToken.id);

      /** @debugFeature formTokens Logs a shortened chain expiration: the starting token id, how many chain tokens were updated and the new expiration. */
      log('token chain expiration shortened', () => ({
        actualTokenId,
        updatedTokenCount,
        expiresAt: expirationTime,
      }));

      userDispatch({ type: SET_FORM_TOKENS, payload: newFormTokenChainState });
    },
    [userDispatch, userState.formTokens]
  );

  const getActualTokenFromUrl = useCallback(() => {
    const tokenId = new URLSearchParams(search).get('token');

    if (!tokenId) {
      /** @debugFeature formTokens Logs when the URL carries no `token` param at all. */
      log('no token param in the URL', () => ({ pathname, search }));
      return null;
    }

    const token = getToken(tokenId);

    if (token !== null) {
      /** @debugFeature formTokens Logs a token successfully resolved from the URL. */
      log('token resolved from the URL', () => ({ tokenId, url: token.url }));
      return token;
    }

    /** @debugFeature formTokens Logs a `token` param with an unknown id — typically an expired or cleaned-up token, or a deep link into another session. */
    log('unknown token id in the URL', () => ({
      tokenId,
      pathname,
      search,
      nodeCount: Object.keys(userState.formTokens.nodes).length,
      rootCount: userState.formTokens.roots.length,
    }));

    return null;
  }, [
    pathname,
    search,
    getToken,
    userState.formTokens.nodes,
    userState.formTokens.roots,
  ]);

  useEffect(() => {
    const expiredTokensCleanup = () => {
      cleanupExpiredTokens();
    };

    expiredTokensCleanup();

    const intervalId = window.setInterval(
      expiredTokensCleanup,
      FOUR_HOURS_LOOP_TIME
    );

    return () => window.clearInterval(intervalId);
  }, []);

  const value: FormTokenChainContextType = useMemo(
    () => ({
      isProvided: true,
      formTokens: userState.formTokens,
      getActualTokenFromUrl,
      cleanupExpiredTokens,
      deleteChainFrom,
      getToken,
      createRootToken,
      createNestedToken,
      updateTokenValues,
      popToken,
      removeDescendantTokens,
      updateParentTokenAndPopActualToken,
      setSmallExpirationTimeToTokenChain,
    }),
    [
      userState,
      getActualTokenFromUrl,
      cleanupExpiredTokens,
      getToken,
      createRootToken,
      createNestedToken,
      updateTokenValues,
      popToken,
      deleteChainFrom,
      removeDescendantTokens,
      updateParentTokenAndPopActualToken,
      setSmallExpirationTimeToTokenChain,
    ]
  );

  return (
    <FormTokenChainContext.Provider value={value}>
      {children}
    </FormTokenChainContext.Provider>
  );
};

export default FormTokenChainProvider;
